const STATUS = {
  drive: { icon: "✅", text: "Credenciales en Drive" },
  revisar: { icon: "🟡", text: "Hay que revisarlo" },
  faltan: { icon: "❌", text: "Faltan credenciales" },
};

// El resumen que ve el usuario antes de dar OK: qué servidores encontró, cuáles ya traen credenciales y cuáles faltan.
export function DiscoveryReview({ discovery, description, systemPrompt, onDescription, onSystemPrompt, excluded = [], onToggleExclude }) {
  const servers = discovery.servers || [];
  const isOut = (s) => excluded.includes(s.key);
  const missing = servers.filter((s) => s.status !== "drive" && !isOut(s));
  const ready = servers.filter((s) => s.status === "drive" && !isOut(s));
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <p data-testid="discovery-docs" className="text-xs text-muted-foreground">
        Leí {discovery.documents?.length || 0} documentos de «{discovery.folder}».
      </p>
      {(discovery.warnings || []).map((w, i) => (
        <p key={i} data-testid="discovery-warning" className="text-xs text-amber-500">⚠️ {w}</p>
      ))}

      {servers.length === 0 ? (
        <p data-testid="discovery-none" className="text-sm text-muted-foreground">
          No encontré servidores en los documentos. El proyecto se creará sin servidores; podrás conectarlos con el candado 🔒 del chat.
        </p>
      ) : (
        <div data-testid="discovery-servers" className="space-y-2">
          {servers.map((s) => {
            const st = STATUS[s.status] || STATUS.faltan;
            const out = isOut(s);
            return (
              <div key={s.key} data-testid={`discovery-server-${s.name}`} data-excluded={out ? "true" : "false"} className={`rounded-lg border border-border p-3 text-sm space-y-1 ${out ? "opacity-50" : ""}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium">
                    {s.name} <span className="text-xs font-normal text-muted-foreground">{s.kind === "routeros" ? "MikroTik (API)" : s.kind === "olt" ? "OLT Huawei (Telnet)" : "Linux (SSH)"}{s.via ? ` · vía ${s.via}` : ""}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs">
                    {out ? "⏸ No se conectará" : `${st.icon} ${st.text}`}
                    {onToggleExclude && (
                      <button
                        type="button"
                        data-testid={`discovery-exclude-${s.name}`}
                        onClick={() => onToggleExclude(s.key)}
                        className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                      >
                        {out ? "Incluir" : "No conectar"}
                      </button>
                    )}
                  </span>
                </div>
                <p className="text-xs font-mono text-muted-foreground">
                  {s.username ? `${s.username}@` : ""}{s.host || "sin IP"}:{s.port}
                </p>
                {s.role && <p className="text-xs text-muted-foreground">{s.role}</p>}
                {(s.notes || []).map((n, i) => (
                  <p key={i} className="text-xs text-amber-500">⚠️ {n}</p>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {missing.length > 0 && (
        <div data-testid="discovery-missing" className="rounded-lg border border-amber-500/40 p-3 space-y-1.5">
          <p className="text-xs font-medium text-amber-500">
            Falta por pegar con el candado 🔒 (después de dar de alta; no tienes que hacer nada más):
          </p>
          {missing.map((s) => (
            <div key={s.key} className="text-xs space-y-0.5">
              <p>❌ <span className="font-medium">{s.name}</span> — falta {s.need}</p>
              {s.example && <p className="font-mono text-muted-foreground break-all">{s.example}</p>}
            </div>
          ))}
        </div>
      )}

      {ready.length > 0 && (
        <p data-testid="discovery-plan" className="text-xs text-muted-foreground">
          Al dar OK: guardo {ready.length} servidor{ready.length > 1 ? "es" : ""} cifrado{ready.length > 1 ? "s" : ""} (los Linux con permiso de cambios: la IA propone y tú confirmas con un botón; un MikroTik o una OLT solo se consulta), pruebo cada conexión, los analizo
          y detecto la base de datos. La IA nunca ve las contraseñas.
        </p>
      )}

      {discovery.databases?.length > 0 && (
        <div data-testid="discovery-databases" className="rounded-lg border border-border p-3 text-sm space-y-1.5">
          <p className="font-medium">Bases de datos que encontré</p>
          {discovery.databases.map((d, i) => (
            <div key={i} data-testid={`discovery-database-${i}`} className="text-xs space-y-0.5">
              <p>
                🗄️ <span className="font-medium">{d.kind}</span>
                {d.server ? ` · en ${d.server}` : ""}
                {d.name ? ` · base «${d.name}»` : ""}
              </p>
              {d.env_path ? (
                <p className="font-mono text-muted-foreground break-all">
                  conexión en {d.env_path}{d.url_var ? ` · variable ${d.url_var}` : ""}{d.name_var ? ` · nombre en ${d.name_var}` : ""}
                </p>
              ) : (
                <p className="text-muted-foreground">Los documentos no dicen dónde está su archivo de conexión; lo busco al analizar el servidor.</p>
              )}
              {d.note && <p className="text-muted-foreground">{d.note}</p>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            La contraseña de la base no se guarda ni se muestra: el panel la lee del archivo de conexión dentro del propio servidor y la IA no la ve.
            Se confirma al analizar el servidor.
          </p>
        </div>
      )}

      {(discovery.services?.length > 0 || discovery.ignored?.length > 0) && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">Informativo (no se conecta)</summary>
          <ul className="mt-1 space-y-0.5">
            {(discovery.services || []).map((s, i) => (
              <li key={`svc-${i}`}>• {s.name}{s.note ? ` — ${s.note}` : ""}</li>
            ))}
            {(discovery.ignored || []).map((s, i) => (
              <li key={`ign-${i}`}>• {s.name}{s.host ? ` (${s.host})` : ""} — ignorado: {s.reason}</li>
            ))}
          </ul>
        </details>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">Descripción y contexto del proyecto (editable)</summary>
        <div className="mt-2 space-y-2">
          <input
            data-testid="project-description-input"
            className="w-full rounded-md border border-border bg-transparent px-2 py-1.5 text-sm"
            value={description}
            onChange={(e) => onDescription(e.target.value)}
          />
          <textarea
            data-testid="project-system-prompt-textarea"
            className="w-full min-h-[160px] rounded-md border border-border bg-transparent px-2 py-1.5 font-mono text-xs"
            value={systemPrompt}
            onChange={(e) => onSystemPrompt(e.target.value)}
          />
        </div>
      </details>
    </div>
  );
}
